import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Package, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { getUserOrders } from '../../../helpers/order';
import _ from 'lodash';

const ITEMS_PER_PAGE = 3;

const BookingsSection = ({ userData }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // console.log('BookingsSection mounted, userData:', userData);
    if (userData?.id) {
      fetchOrders();
    } else {
      setLoading(false);
      // console.log('No user data available');
    }
  }, [userData]);

  const fetchOrders = async () => {
    // console.log('Fetching orders for user ID:', userData.id);
    try {
      const userOrders = await getUserOrders(userData.id);
      // console.log('Received orders:', userOrders);
      
      if (Array.isArray(userOrders)) {
        setOrders(userOrders);
      } else {
        console.error('Received invalid orders format:', userOrders);
        setError('Format de données invalide');
      }
    } catch (error) {
      console.error('Error in fetchOrders:', error);
      setError('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning';
      case 'confirmed':
        return 'bg-success';
      case 'cancelled':
        return 'bg-danger';
      case 'completed':
        return 'bg-secondary';
      default:
        return 'bg-info';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'confirmed':
        return 'Confirmée';
      case 'cancelled':
        return 'Annulée';
      case 'completed':
        return 'Terminée';
      default:
        return status;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return date;
    }
  };

  // Pagination
  const totalPages = Math.ceil(orders.length / ITEMS_PER_PAGE);
  const paginatedOrders = orders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-danger mb-0" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="card-title mb-4">Mes réservations</h3>

        {orders.length === 0 ? (
          <div className="text-center py-5">
            <Package size={48} className="text-muted mb-3" />
            <p className="mb-0">Vous n'avez pas encore de réservations</p>
          </div>
        ) : (
          <>
            <div className="row g-4">
              {paginatedOrders.map((order) => {
                // Group items by product, dates, and price
                const groupedItems = _.groupBy(order.order_items, item => 
                  `${item.product?.id}-${item.start_date}-${item.end_date}-${item.price}`
                );

                return (
                  <div key={order.id} className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="card-title mb-1">
                              Commande #{order.document_id || order.id}
                            </h5>
                            <div className="text-muted small">
                              <Clock size={14} className="me-1" />
                              Commandé le {formatDate(order.createdAt)}
                            </div>
                          </div>
                          <span className={`badge ${getStatusBadgeClass(order.order_status)}`}>
                            {getStatusLabel(order.order_status)}
                          </span>
                        </div>

                        {Object.values(groupedItems).map((items, index) => {
                          const item = items[0]; // Take first item as reference
                          const quantity = items.length;
                          
                          return (
                            <div key={index} className="border-top pt-3 mt-3">
                              <div className="row">
                                <div className="col-md-8">
                                  <h6 className="mb-1">
                                    {item.product?.titre}
                                    {quantity > 1 && (
                                      <span className="ms-2 badge bg-secondary">x{quantity}</span>
                                    )}
                                  </h6>
                                  <div className="text-muted small">
                                    <Calendar size={14} className="me-1" />
                                    Du {formatDate(item.start_date)} au {formatDate(item.end_date)}
                                  </div>
                                </div>
                                <div className="col-md-4 text-end">
                                  <span className="fw-bold">
                                    {(item.price * quantity).toFixed(2)}€
                                  </span>
                                  {quantity > 1 && (
                                    <small className="text-muted d-block">
                                      ({item.price.toFixed(2)}€ x {quantity})
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div className="border-top pt-3 mt-3">
                          <div className="d-flex justify-content-between">
                            <span>Livraison <small>({order.delivery_method == 'home_delivery' ? 'À domicile' : 'En point relais'})</small></span>
                            <span className="fw-bold">{order.delivery_price}€</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Total</span>
                            <span className="fw-bold">{order.total}€</span>
                          </div>
                          {order.deposit > 0 && (
                            <div className="d-flex justify-content-between text-muted small mt-1">
                              <span>Caution</span>
                              <span>{order.deposit}€</span>
                            </div>
                          )}
                        </div>

                        {order.comments && (
                          <div className="border-top pt-3 mt-3">
                            <p className="text-muted small mb-0">
                              <strong>Commentaires :</strong> {order.comments}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Navigation des réservations">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={16} />
                      </button>
                    </li>
                    
                    {[...Array(totalPages)].map((_, index) => (
                      <li
                        key={index + 1}
                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingsSection;